import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const PLACEHOLDER_IMG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='100%25' height='100%25' fill='%23F1F7EC'/%3E%3Ctext x='50%25' y='50%25' font-family='sans-serif' font-size='20' fill='%237E8A7B' text-anchor='middle' dominant-baseline='middle'%3ENo image%3C/text%3E%3C/svg%3E";

export const resolveImageUri = (uri) =>
  uri && uri.startsWith("/") ? `${BASE}${uri}` : (uri || PLACEHOLDER_IMG);

const http = axios.create({ baseURL: API });

http.interceptors.request.use((config) => {
  const t = typeof window !== "undefined" ? sessionStorage.getItem("admin_token") : null;
  if (t) {
    config.headers["Authorization"] = `Bearer ${t}`;
    config.headers["X-Admin-Session"] = t;
  }
  return config;
});

const data = (p) => p.then((r) => r.data);
const err = (e) => {
  const d = e?.response?.data?.detail;
  throw new Error(typeof d === "string" ? d : d ? JSON.stringify(d) : e.message);
};

export const api = {
  // products
  listProducts: (category) =>
    data(http.get(`/products`, { params: category && category !== "All" ? { category } : {} })).catch(err),
  getProduct: (id) => data(http.get(`/products/${id}`)).catch(err),
  categories: () => data(http.get(`/categories`)).then((j) => j.categories ?? []).catch(err),
  addCategory: (name) => data(http.post(`/categories`, { name })).catch(err),
  reorderCategories: (names) => data(http.put(`/categories/reorder`, { names })).catch(err),
  renameCategory: (old_name, new_name) => data(http.put(`/categories/rename`, { old_name, new_name })).catch(err),
  deleteCategory: (name) => data(http.delete(`/categories`, { params: { name } })).catch(err),
  createProduct: (d) => data(http.post(`/products`, d)).catch(err),
  updateProduct: (id, d) => data(http.put(`/products/${id}`, d)).catch(err),
  deleteProduct: (id) => data(http.delete(`/products/${id}`)).catch(err),
  importProductUrl: (url) => data(http.post(`/products/import-url`, { url })).catch(err),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return data(http.post(`/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } })).catch(err);
  },
  // reviews
  getReviews: (id) => data(http.get(`/products/${id}/reviews`)).catch(err),
  addReview: (id, d) => data(http.post(`/products/${id}/reviews`, d)).catch(err),
  adminListReviews: (status) => data(http.get(`/admin/reviews`, { params: { status } })).catch(err),
  moderateReview: (id, approved) => data(http.post(`/reviews/${id}/moderate`, { approved })).catch(err),
  deleteReview: (id) => data(http.delete(`/reviews/${id}`)).catch(err),
  // instant BV
  requestInstantBv: (id) => data(http.post(`/orders/${id}/instant-bv`)).catch(err),
  markInstantBvDone: (id) => data(http.post(`/orders/${id}/instant-bv/done`)).catch(err),
  // shiprocket
  shiprocketStatus: () => data(http.get(`/shiprocket/status`)).catch(err),
  shiprocketConnect: (email, password) => data(http.post(`/shiprocket/connect`, { email, password })).catch(err),
  shiprocketDisconnect: () => data(http.post(`/shiprocket/disconnect`)).catch(err),
  shiprocketSync: () => data(http.post(`/shiprocket/sync`)).catch(err),
  // twilio whatsapp notifications
  twilioStatus: () => data(http.get(`/twilio/status`)).catch(err),
  twilioConnect: (account_sid, auth_token, from_number) => data(http.post(`/twilio/connect`, { account_sid, auth_token, from_number })).catch(err),
  twilioDisconnect: () => data(http.post(`/twilio/disconnect`)).catch(err),
  twilioTest: (phone) => data(http.post(`/twilio/test`, { phone })).catch(err),
  twilioSaveTemplates: (tmpl_shipped, tmpl_delivered, tmpl_new_order) => data(http.put(`/twilio/templates`, { tmpl_shipped, tmpl_delivered, tmpl_new_order })).catch(err),
  // banners
  listBanners: () => data(http.get(`/banners`)).catch(() => []),
  addBanner: (image) => data(http.post(`/banners`, { image })).catch(err),
  deleteBanner: (id) => data(http.delete(`/banners/${id}`)).catch(err),
  // diseases (shop by concern)
  listDiseases: () => data(http.get(`/diseases`)).catch(err),
  getDisease: (id) => data(http.get(`/diseases/${id}`)).catch(err),
  diseaseProducts: (id) => data(http.get(`/diseases/${id}/products`)).catch(err),
  createDisease: (d) => data(http.post(`/diseases`, d)).catch(err),
  updateDisease: (id, d) => data(http.put(`/diseases/${id}`, d)).catch(err),
  deleteDisease: (id) => data(http.delete(`/diseases/${id}`)).catch(err),
  // certificates
  listCertificates: () => data(http.get(`/certificates`)).catch(err),
  addCertificate: (image) => data(http.post(`/certificates`, { image })).catch(err),
  deleteCertificate: (id) => data(http.delete(`/certificates/${id}`)).catch(err),
  // posts (blogs & vlogs)
  listPosts: () => data(http.get(`/posts`)).catch(err),
  getPost: (id) => data(http.get(`/posts/${id}`)).catch(err),
  createPost: (d) => data(http.post(`/posts`, d)).catch(err),
  updatePost: (id, d) => data(http.put(`/posts/${id}`, d)).catch(err),
  deletePost: (id) => data(http.delete(`/posts/${id}`)).catch(err),
  uploadVideo: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return data(http.post(`/upload/video`, fd, { headers: { "Content-Type": "multipart/form-data" } })).catch(err);
  },
  // pages
  updatePage: (key, d) => data(http.put(`/pages/${key}`, d)).catch(err),
  // settings
  updateSettings: (d) => data(http.put(`/settings`, d)).catch(err),
  // registrations
  createRegistration: (d) => data(http.post(`/registrations`, d)).catch(err),
  listRegistrations: () => data(http.get(`/registrations`)).catch(err),
  deleteRegistration: (id) => data(http.delete(`/registrations/${id}`)).catch(err),
  setRegistrationContacted: (id, contacted) => data(http.post(`/registrations/${id}/contacted`, { contacted })).catch(err),
  // reports
  reportMonths: () => data(http.get(`/reports/sales/available-months`)).catch(err),
  salesReportUrl: (month, format) => `${API}/reports/sales?month=${month}&format=${format}`,
  // checkout
  createOrder: (payload) => data(http.post(`/checkout/create-order`, payload)).catch(err),
  demoComplete: (order_id) => data(http.post(`/checkout/demo-complete`, { order_id })).catch(err),
  verify: (payload) => data(http.post(`/checkout/verify`, payload)).catch(err),
  getOrder: (id) => data(http.get(`/orders/${id}`)).catch(err),
  // orders
  listOrders: () => data(http.get(`/orders`)).catch(err),
  lookupOrders: (phone) => data(http.get(`/orders/lookup`, { params: { phone } })).catch(err),
  cancelOrder: (id, phone) => data(http.post(`/orders/${id}/cancel`, { phone })).catch(err),
  updateOrderStatus: (id, d) => data(http.patch(`/orders/${id}/status`, d)).catch(err),
  invoiceUrl: (id) => `${API}/orders/${id}/invoice`,
  // coupons
  listCoupons: () => data(http.get(`/coupons`)).catch(err),
  createCoupon: (d) => data(http.post(`/coupons`, d)).catch(err),
  updateCoupon: (id, d) => data(http.put(`/coupons/${id}`, d)).catch(err),
  deleteCoupon: (id) => data(http.delete(`/coupons/${id}`)).catch(err),
  availableCoupons: (altos, phone, subtotal) =>
    data(http.get(`/coupons/available`, { params: { altos, phone, subtotal } })).catch(() => []),
  validateCoupon: (d) => data(http.post(`/coupons/validate`, d)).catch(err),
  // delivery
  deliveryEstimate: (pincode) => data(http.get(`/delivery-estimate`, { params: { pincode } })).catch(err),
  // settings / pages
  getSettings: () => data(http.get(`/settings`)).catch(err),
  getPage: (key) => data(http.get(`/pages/${key}`)).catch(err),
  // admin
  verifyAdminPin: (pin) => data(http.post(`/admin/verify-pin`, { pin })).catch(err),
  changeAdminPin: (current_pin, new_pin) => data(http.post(`/admin/change-pin`, { current_pin, new_pin })).catch(err),
  // altos id login (session-only, verified server-side, no storage)
  altosLogin: (ccode, password) => data(http.post(`/altos/login`, { ccode, password })).catch(err),
  // retail billing (altos id holders)
  getBillingProfile: (altos_id) => data(http.get(`/billing/profile`, { params: { altos_id } })).catch(err),
  saveBillingProfile: (payload) => data(http.post(`/billing/profile`, payload)).catch(err),
  listBillingProducts: (altos_id) => data(http.get(`/billing/products`, { params: { altos_id } })).catch(() => []),
  addBillingProduct: (payload) => data(http.post(`/billing/products`, payload)).catch(err),
  deleteBillingProduct: (id, altos_id) => data(http.delete(`/billing/products/${id}`, { params: { altos_id } })).catch(err),
  createBill: (payload) => data(http.post(`/billing/bills`, payload)).catch(err),
  listBills: (altos_id) => data(http.get(`/billing/bills`, { params: { altos_id } })).catch(() => []),
  billPdfUrl: (id) => `${API}/billing/bills/${id}/pdf`,
  adminBillingProfiles: (type = "") => data(http.get(`/admin/billing/profiles`, { params: { type } })).catch(err),
  root: () => data(http.get(`/`)).catch(err),
};

export const formatINR = (v) =>
  "₹" + Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
